import { useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import { useAppStore } from "@/store";
import { useOrgParameter, useUpdateParameter } from "@/hooks/use-parameters";
import { useToast } from "@/hooks/use-toast";
import { PARAMETER_CATEGORIES } from "@/components/parameters/shared-constants";
import {
  PageHeader,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  FormField,
  Select,
  PageSkeleton,
} from "@/components/ui";
import { inputClass } from "@/components/ui/form-field";

type ParameterCategory = components["schemas"]["ParameterCategory"];

export const Route = createFileRoute("/_authenticated/parameters/$parameterId/edit")({
  component: EditParameterPage,
});

interface FormState {
  name: string;
  unit: string;
  category: string;
  cas_number: string;
  description: string;
}

function EditParameterPage() {
  const { t } = useTranslation("parameters");
  const navigate = useNavigate();
  const toasts = useToast();
  const { parameterId } = Route.useParams();
  const orgId = useAppStore((s) => s.activeOrgId);

  const { data: parameter, isLoading } = useOrgParameter(orgId, parameterId);
  const mutation = useUpdateParameter(orgId, parameterId);

  const [form, setForm] = useState<FormState>({
    name: "",
    unit: "",
    category: "",
    cas_number: "",
    description: "",
  });
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (parameter && !initialized) {
      setForm({
        name: parameter.name,
        unit: parameter.unit,
        category: parameter.category ?? "",
        cas_number: parameter.cas_number ?? "",
        description: parameter.description ?? "",
      });
      setInitialized(true);
    }
  }, [parameter, initialized]);

  const [errors, setErrors] = useState<{ name?: string; unit?: string }>({});

  const updateField = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = (): boolean => {
    const newErrors: { name?: string; unit?: string } = {};
    if (!form.name.trim())
      newErrors.name = t("messages.fieldRequired", { field: t("columns.name") });
    if (!form.unit.trim())
      newErrors.unit = t("messages.fieldRequired", { field: t("columns.unit") });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    mutation.mutate(
      {
        name: form.name.trim(),
        unit: form.unit.trim(),
        category: (form.category || undefined) as ParameterCategory | undefined,
        cas_number: form.cas_number.trim() || undefined,
        description: form.description.trim() || undefined,
      },
      {
        onSuccess: (result) => {
          toasts.success("parameters:messages.updateSuccess");
          const warnings = result?.warnings;
          if (warnings && warnings.length > 0) {
            for (const w of warnings) {
              toasts.error(w);
            }
          }
          void navigate({
            to: "/parameters/$parameterId",
            params: { parameterId },
          });
        },
        onError: () => {
          toasts.error("parameters:messages.updateFailed");
        },
      },
    );
  };

  const navigateBack = () => {
    void navigate({
      to: "/parameters/$parameterId",
      params: { parameterId },
    });
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl p-8">
        <PageSkeleton />
      </div>
    );
  }

  if (!parameter || parameter.source !== "custom") {
    return (
      <div className="mx-auto max-w-2xl p-8">
        <PageHeader title={t("messages.notFound")} />
      </div>
    );
  }

  const breadcrumbs = [
    { label: t("title"), to: "/parameters" },
    { label: parameter.name, to: `/parameters/${parameterId}` },
    { label: t("editTitle") },
  ];

  const categorySelectOptions = [
    { value: "", label: "—" },
    ...PARAMETER_CATEGORIES.map((opt) => ({
      value: opt.value,
      label: t(opt.labelKey),
    })),
  ];

  return (
    <div className="mx-auto max-w-2xl p-8">
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader title={t("editTitle")} />

      <form onSubmit={handleSubmit} className="mt-6">
        <Card>
          <CardContent className="space-y-4 p-6">
            <FormField label={t("columns.code")}>
              <input
                type="text"
                className={`${inputClass} cursor-not-allowed opacity-60`}
                value={parameter.code}
                disabled
              />
            </FormField>

            <FormField label={t("columns.name")} required error={errors.name}>
              <input
                type="text"
                className={inputClass}
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
              />
            </FormField>

            <FormField
              label={t("columns.unit")}
              required
              error={errors.unit}
              hint={t("messages.unitChangeWarning")}
            >
              <input
                type="text"
                className={inputClass}
                value={form.unit}
                onChange={(e) => updateField("unit", e.target.value)}
              />
            </FormField>

            <FormField label={t("columns.category")}>
              <Select
                value={form.category}
                onChange={(e) => updateField("category", e.target.value)}
                options={categorySelectOptions}
              />
            </FormField>

            <FormField label={t("columns.casNumber")}>
              <input
                type="text"
                className={inputClass}
                value={form.cas_number}
                onChange={(e) => updateField("cas_number", e.target.value)}
              />
            </FormField>

            <FormField label={t("columns.description")}>
              <textarea
                className={`${inputClass} min-h-20 resize-y`}
                value={form.description}
                onChange={(e) => updateField("description", e.target.value)}
                rows={3}
              />
            </FormField>
          </CardContent>
        </Card>

        <div className="mt-6 flex gap-3">
          <Button type="submit" disabled={mutation.isPending}>
            {t("actions.save")}
          </Button>
          <Button type="button" variant="secondary" onClick={navigateBack}>
            {t("actions.cancel")}
          </Button>
        </div>
      </form>
    </div>
  );
}
